// VehItinDailyList.js
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import { Button, IconButton, TextField, FormControl, InputLabel, OutlinedInput, InputAdornment } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import TimelineIcon from '@mui/icons-material/Timeline';
import ResponseAlert from '../message/ResponseAlert';
import FlexyTable from '../common/flexytable/FlexyTable';
import ValueUtil from '../../model/ValueUtil';
import { AppNumber, AppWord, AppObject, AppPalette, TableColumnType } from '../../model/AppConst';
import { useLazyQuery } from '@apollo/client';
import { LIST_DAILY_VEH_ITIN } from './VehItinGql';

const VehItinDailyListContainer = styled(Box)({
	flexGrow:1, display:'flex', flexDirection:'column'
});

const HeaderBox = styled(Box)({
    display: 'flex', flexWrap: 'wrap', height: AppNumber.HeaderToolBoxHeight, padding: AppNumber.SmallBoxPadding,
    alignItems: 'center', justifyContent: 'space-between'
});

const ControllerBox = styled(Box)({
    display: 'flex', alignItems: 'center'
});

const TabBox = styled(Box)({
    flexGrow: 1, textAlign: 'right', display: 'flex', alignItems: 'center', paddingLeft: 20
});

const TableColumns = [
    { id: 'vehPlates', label: '차량번호', width: 110, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'location', label: '위치', width: 320, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'distBetween', label: '이동거리(Km)', width: 100, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'runSec', label: '이동시간(분)', width: 100, show: true, option:{align: 'right', print:(r,i,v)=>{return ValueUtil.formatDurationAsMinutes(v)}} },
    { id: 'timeArrival', label: '도착', width: 160, show: true, option:{align: 'center', type: TableColumnType.DATETIME} },
    { id: 'staySec', label: '정차(분)', width: 100, show: true, option:{align: 'right', print:(r,i,v)=>{return ValueUtil.formatDurationAsMinutes(v)}} },
    { id: 'timeDeparture', label: '출발', width: 160, show: true, option:{align: 'center', type: TableColumnType.DATETIME} },
];

function addDaysToYmd(ymd, days) {
    const d = ValueUtil.parseDate(ymd);
    d.setDate(d.getDate() + days);
    return ValueUtil.getYmdOfDate(d);
}

export default function VehItinDailyList({
    tabSelector,
	maxHeight,
	maxWidth,
    onRequestViewRoute,
}) {
    const [routeDate, setRouteDate] = useState(ValueUtil.getYmdText());
    const [records, setRecords] = useState([]);
    const [search, setSearch] = useState('');
    const [responseAlert, setResponseAlert] = useState(null);

    // ##### Call GraphQL to get List #####
    const [getVehItinDailyList, responseList] = useLazyQuery(LIST_DAILY_VEH_ITIN, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => {setResponseAlert({open:true, error: error, title: "전체차량 동정정보 조회 오류"})}
    });

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehItinDailyList) {
            setRecords(data.vehItinDailyList.map(item => ({...item, dataKey: item.vehId + '_' + item.keyTime})));
        }
    };

    const onChangeSearch = (event) => {
        setSearch(event.target.value);
    };

    const onChangeDate = (event) => {
        setRouteDate(event.target.value);
    };

    const onClickToGetList = () => {
        if(routeDate) {
            getVehItinDailyList({
                variables: {
                    timeFrom: routeDate + ' 00:00:00',
                    timeTill: addDaysToYmd(routeDate, 1) + ' 00:00:00'}
            });
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        if(toolId === AppWord.ROUTE) {
            if(onRequestViewRoute) {
                onRequestViewRoute({vehId:row.vehId, vehPlates:row.vehPlates}, row.timePrevStop, row.timeArrival);
            }
        }
    };

    return(
        <VehItinDailyListContainer>
            <HeaderBox>
				<FormControl variant='outlined' size={AppWord.SMALL}>
					<InputLabel htmlFor="search-VehItinDaily-list" style={{backgroundColor:'white'}}>검색</InputLabel>
					<OutlinedInput id="search-VehItinDaily-list" onChange={onChangeSearch}
						value={search}
						disabled={records.length === 0}
						size={AppWord.SMALL}
						endAdornment={
							Boolean(search) ?
							<InputAdornment position={AppWord.END}>
								<IconButton
								onClick={()=>{setSearch('')}}
								size={AppWord.SMALL}
								edge={AppWord.END}
								>
									<ClearIcon />
								</IconButton>
							</InputAdornment>
							:
							null
						}
					/>
				</FormControl>
                &nbsp;&nbsp;
                {/* <ControllerBox> */}
                    <TextField id='routeDate' type='date' value={routeDate} size={AppWord.SMALL} onChange={onChangeDate} />
                    &nbsp;&nbsp;
                    <Button
                        variant={AppPalette.VariantContained}
                        color={AppPalette.PrimaryColor}
                        disabled={!Boolean(routeDate)}
                        onClick={onClickToGetList}
                        style={{marginLeft:10}}
                    >
                        <DownloadIcon fontSize={AppWord.SMALL} />
                        조회
                    </Button>
                    <TabBox>
                        {tabSelector}
                    </TabBox>
                {/* </ControllerBox> */}
            </HeaderBox>
            <FlexyTable
                name="veh_itin_daily_list"
                uniqueKey="dataKey"
                columns={TableColumns}
                records={records}
                search={search}
                tools={[{id:AppWord.ROUTE, icon:<TimelineIcon fontSize={AppWord.SMALL} color="primary" />}]}
                onClickTool={onClickToolOnRow}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </VehItinDailyListContainer>
    );
}
